import type { Location, Path } from 'react-router'

import buildURL from '../utils/buildURL'
import getLocaleFromPath from '../utils/getLocaleFromPath'

export function generateCategoryUrl(pageSlug: string) {
  return encodeURI(`/c/${pageSlug}`)
}

export function generatePageUrl(pageType: string, pageSlug: string): string {
  switch (pageType) {
    case 'blank':
      return encodeURI(`/i/${pageSlug}`)
    case 'legal':
      return encodeURI(`/l/${pageSlug}`)
    case 'category':
      return generateCategoryUrl(pageSlug)
    default:
      return ''
  }
}

export function withQueryAndScope(to: string | Partial<Path>, currentLocation: Location) {
  const paramKeys = [
    'preview',
    'previewTheme',
    'previewThemeSettings',
    'shop',
    'language',
    'token',
    'themeTitle',
    'isNowPreview',
  ]

  const query = new URLSearchParams(currentLocation.search)

  const params = paramKeys.reduce((result, key) => {
    const value = query.get(key)

    if (value) result[key] = value

    return result
  }, {})

  // https://github.com/remix-run/react-router/blob/v3.2.6/docs/API.md#to
  let pathname, pathnameWithoutParams, existingParams, hash
  if (typeof to === 'string') {
    ;[pathname, hash = ''] = to.split('#')
    if (hash) {
      hash = '#' + hash
    }
    ;[pathnameWithoutParams, existingParams] = pathname.split('?')
  } else {
    pathname = pathnameWithoutParams = to.pathname ?? ''
    existingParams = buildURL('', new URLSearchParams(to.search)).substring(1)
    hash = to.hash ?? ''
  }

  const locale = getLocaleFromPath(currentLocation.pathname)
  const pathLocale = locale && !pathname.startsWith(`/${locale}`) ? `/${locale}` : ''

  const scope = currentLocation.pathname.startsWith('/editor') ? `/editor${pathLocale}` : pathLocale

  // Makes `withQueryAndScope` usable if one of the param keys is already in the given url
  const uniqParams = (existingParams ?? '').split('&').reduce((acc, pair) => {
    const [key, value] = pair.split('=')

    // Its already separated from &, = symbols, so we can go with only replacing + and then decoding
    const valueDecoded = value === undefined ? value : decodeURIComponent(value.replace(/\+/g, '%20'))

    return paramKeys.includes(key) ? acc : { [key]: valueDecoded, ...acc }
  }, params)

  return typeof to === 'string'
    ? buildURL(scope + pathnameWithoutParams, uniqParams) + hash
    : {
        pathname: scope + pathnameWithoutParams,
        query: uniqParams,
        search: buildURL('', uniqParams),
        hash,
      }
}
